<template>
  <div
    class="w-full p-4 relative rounded bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-600 invalid:focus:ring-red-200 invalid:hover:border-red-500 transition-all duration-200 hover:border-gray-400 dark:hover:border-gray-600"
    :class="[{ 'border-red-400': props.data.invalid }]"
  >
    <div class="flex items-center justify-between -mx-1">
      <div
        class="flex items-start sm:items-center flex-col sm:flex-row flex-1 px-1"
      >
        <span class="text-[12px] sm:text-[14px] font-medium mr-2 leading-6">
          {{ props.data.label }} 
        </span>

        <span
          v-if="props.data.subLabel"
          class="text-[12px] font-light italic"
        >
          {{ props.data.subLabel }}
        </span>

        <HelpersTooltipHelpers 
          v-if="props.data.tooltip || props.data.helpersData"
          :data="{
            parentClasses: 'px-2 ml-0 inline-block',
            tooltip: props.data.tooltip,
            helpersData: props.data.helpersData
          }"
        />
      </div>

      <div class="flex items-center space-x-2 px-1">
        <Button
          v-if="showActionButtons"
          v-tooltip.bottom="props.data.tooltip"
          :label="isFieldsAreFilled ? $t('edit') : $t('add')"
          :icon="isFieldsAreFilled ? 'icon icon-pencil' : 'icon icon-e-add'"
          size="supersmall"
          variant="outlined"
          severity="secondary"
          :pt-options="{
            variant: 'extra'
          }"
          :disabled="props.data.disabled"
          @click="drawerOptions.formData = {
            header: props.data.additionalOptions?.drawerLabel ? props.data.additionalOptions?.drawerLabel : props.data.label,
            formDataCurrent: props.formData,
            autocloseDrawerAfterExternalUpload: props.data.additionalOptions?.autocloseDrawerAfterExternalUpload,
            fields: props.fields,
            fieldType: props.fieldType,
            returnData: async (e) => {
              emit('returnData', e)
            }
          }"
        />

        <Button
          v-if="isFieldsAreFilled && !props.data.pending && props.fieldType !== 'projectsList'"
          :label="null"
          icon="icon icon-delete-forever"
          size="supersmall"
          severity="danger"
          variant="outlined"
          :pt-options="{
            variant: 'extra'
          }"
          :disabled="props.data.disabled"
          @click="confirmOptions({
            message: $t('cleaningTheFieldDescr', { name: props.data.label }),
            accept: () => { 
              clearFields()
            }
          })"
        />
      </div>
    </div>

    <div
      v-if="isFieldsAreFilled"
      class="mt-4"
    >
      <template v-if="props.fieldType === 'greetingVideo'">
        <div>
          <FormsGeneratorSubFieldsVideo 
            :data="props.formData[props.data.name]"
            :link-to="props.data.linkTo"
            :link-type="props.data.linkType"
            :label-name="props.data.altLabel"
            :error="props.data.error"
            :pending="props.data.pending"
            @return-data="(e) => { 
              const newData = JSON.parse(JSON.stringify(props.formData))
              newData[props.data.name] = e

              emit('returnData', newData)
            }"
          />
        </div>
      </template>

      <template v-else-if="props.fieldType === 'projectsList'">
        <div>
          <Button
            class="text-sm text-blue-600 underline !opacity-100"
            :unstyled="true"
            :link="true"
            @click="drawerOptions.formData = {
              header: props.data.additionalOptions?.drawerLabel ? props.data.additionalOptions?.drawerLabel : props.data.label,
              formDataCurrent: props.formData,
              autocloseDrawerAfterExternalUpload: props.data.additionalOptions?.withautocloseDrawerAfterExternalUploadUploader,
              fields: props.fields,
              fieldType: props.fieldType,
              returnData: async (e) => {
                emit('returnData', e)
              }
            }"
          >
            {{ $t('selected') }}: {{ props.data.additionalOptions?.projectsData?.length }}
          </Button>

          <div class="mt-4">
            <FormsGeneratorSubFieldsSelectedProjects 
              :data="props.data.additionalOptions?.projectsData"
            />
          </div>
        </div>
      </template>

      <template v-else>
        <template
          v-for="field, i in props.fields"   
        >
          <div
            v-if="isShowField(field)"
            :key="i"
            class="flex text-sm border-t border-l border-r last:border-b"
          >
            <div class="w-[150px] flex-0 bg-surface-100 px-4 py-2">
              {{ field.label }}:
            </div>
            
            <div class="w-[calc(100% - 150px)] flex-1 px-4 py-2">
              <NuxtLink
                v-if="/^https:\/\/s3\.amazonaws\.com\/.*\.[a-z0-9]+$/i.test(props.formData[field.name]) && sanitizeField(props.formData[field.name])"
                :to="sanitizeField(props.formData[field.name])"
                target="_blank"
                class="underline"
              >
                {{ $t('preview') }}
              </NuxtLink>

              <template v-else>
                {{ props.fields.find(n => n?.additionalOptions?.leaveBlank)?.name === field?.name && (props.formData[field.name] === true || props.formData[field.name] === 1)
                  ? $t('yes')
                  : sanitizeField(props.formData[field.name]) }}
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
const { drawerOptions, confirmOptions } = usePopups()
const { deleteVimeo } = useGeneral()

/**
* Props
*/
const props = defineProps({
  data: {
    type: [Object],
    default: null
  },
  fields: {
    type: [Array],
    default: null
  },
  formData: {
    type: [Object],
    default: null
  },
  fieldType: {
    type: [String],
    default: null
  },
  callback: {
    type: [Function],
    default: null
  }
})

/**
* Emits
*/
const emit = defineEmits(['returnData'])

/**
* Is Show Field? (Logix for leaveBlank field)
*/
const isShowField = (field) => { 
  if (field.isField === false || field.enabled === false) { 
    return false
  }

  if (props.fields.find(n => n?.additionalOptions?.leaveBlank)) {
    if (props.formData[props.fields.find(n => n?.additionalOptions?.leaveBlank)?.name]) {
      if (field.name === props.fields.find(n => n?.additionalOptions?.leaveBlank)?.name) {
        return true
      }
    } else { 
      if (field.name === props.fields.find(n => n?.additionalOptions?.leaveBlank)?.name) {
        return false
      } else {
        return true
      }
    }
  } else { 
    return true
  }
  
  return false
}

/**
* Fields Are Filled?
*/
const isFieldsAreFilled = computed({
  get() {
    if (props.fieldType === 'projectsList') {
      if (props.formData[props.data.name]?.length || props.data.additionalOptions?.projectsData?.length > 0) {
        return true
      }
    } else if ((props.data.isGroup && (props.formData[props.data.name] || props.data.pending || props.data.error))) {
      if (props.formData[props.data.name]?.deleted === true) {
        return false
      } else {
        return true
      }
    } else {
      for (const field of props.fields) {
        if (props.formData[field.name]) {
          return true
        }
      }
    }

    return false
  }
})



/**
* Show Action Buttons?
*/
const showActionButtons = computed({
  get() {
    if (props.fieldType === 'greetingVideo' && (props.formData[props.data.name] || props.data.pending) && props.formData[props.data.name]?.deleted !== true) {
      return false
    }

    return true
  }
})

/**
* Santize string
*/
const sanitizeField = (field) => {
  return (field === null || typeof field === 'undefined' || typeof field?.replace  === 'undefined') ? '' : field?.replace(/&nbsp;/g, ' ')
}

/**
* Clear Fielsd
*/
const clearFields = () => {
  const newData = JSON.parse(JSON.stringify(props.formData))

  /**
  * If it Video
  */
  if (props.fieldType === 'greetingVideo' && props.data.name && props.formData[props.data.name] && props.formData[props.data.name].url && props.data?.additionalOptions?.dynamicDelete !== false) {
    deleteVimeo({
      video_url: props.formData[props.data.name].url
    })
  }

  /**
  * Clear Fields
  */
  if (props.data.isGroup) {
    if (props.data?.additionalOptions?.dynamicDelete === false) {
      newData[props.data.name].deleted = true
    } else {
      newData[props.data.name] = null
    }
  } else {
    for (const field of props.fields) {
      if (props.formData[field.name]) {
        if (props.data?.additionalOptions?.dynamicDelete === false) {
          newData[field.name].deleted = true
        } else {
          newData[field.name] = null
        }
      }
    }
  }

  /**
  * Return
  */
  emit('returnData', newData)

  /**
  * Reset props.data.pending and props.data.error
  */
  if (typeof props.callback === 'function') {
    props.callback({
      type: 'reset'
    })
  }
}
</script>

